"use client";

import { useEffect } from "react";
import { useFcmToken } from "@/context/fcmcontext";
import { saveFcmToken } from "@/app/api/service/firebase";
import { FCM } from "../interface/homepage";
import { useAuth } from "@/context/authcontext";

const VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

function validateVapidKey(key: string | undefined): boolean {
    if (!key) {
        console.error("❌ Missing VAPID key");
        return false;
    }

    try {
        let paddedKey = key;
        const pad = key.length % 4;
        if (pad) {
            paddedKey += "=".repeat(4 - pad);
        }

        const standardKey = paddedKey.replace(/-/g, "+").replace(/_/g, "/");
        atob(standardKey);
        return true;
    } catch (e) {
        console.error("❌ Invalid VAPID key format:", e);
        return false;
    }
}

export default function FCMInit({ setMessage }: { setMessage?: (message: FCM) => void }) {
    const { token, refreshToken } = useFcmToken();
    const { isAuthenticated, isAuthFetched } = useAuth();

    // Ensure token refresh runs for authenticated sessions (provider still
    // skips Firebase entirely until permission is granted / refreshToken is called).
    useEffect(() => {
        if (!isAuthFetched || !isAuthenticated) return;
        void refreshToken();
        // refreshToken is intentionally omitted: provider recreates it each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthFetched, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || !token) return;

        let unsubscribe: (() => void) | undefined;

        const setupFCM = async () => {
            try {
                if (!validateVapidKey(VAPID_KEY)) return;

                const [{ getMessaging, onMessage }, { getFirebaseApp }] = await Promise.all([
                    import("firebase/messaging"),
                    import("@/app/lib/firebase-client"),
                ]);

                const messaging = getMessaging(getFirebaseApp());

                await saveFcmToken(token);

                unsubscribe = onMessage(messaging, (payload) => {
                    if (payload.data?.type !== "pushNote") {
                        setMessage?.(payload.data as unknown as FCM);
                    } else {
                        setMessage?.({
                            ...payload.data,
                            title: payload.notification?.title || "Notification",
                            body: payload.notification?.body || "You have a new message.",
                            url: payload.data?.url || process.env.ICM_URL,
                        } as unknown as FCM);
                    }
                });
            } catch (error) {
                console.error("❌ Error setting up FCM:", error);
            }
        };

        void setupFCM();

        return () => {
            unsubscribe?.();
        };
    }, [token, setMessage, isAuthenticated]);

    return null;
}
